import { LogService } from "@/services/logging";
import BookingModule from "@/store/modules/booking";
import POSConfigModule from "@/store/modules/pos-config";

/**
 * functions used in TEST env only to call seats io endpoints
 * in PROD, we shouldn't expose API keys.
 * In PROD, POS will call backend endpoints (still in progress) instead of seats io endpoints
 */

// PC.resourceEventKey may carry a comma-separated list of event keys
// (membership seating plan, type 17). Splits to a clean array of trimmed keys.
function parseEventKeys(eventKey: string): string[] {
  return eventKey
    .split(",")
    .map((k) => k.trim())
    .filter(Boolean);
}

function getAdminKey(): string {
  console.log("hi from getAdminKey");
  const adminKey = String(POSConfigModule.groupInfo?.seatsPrivateKey);
  LogService.info(`adminKey for seats io ${adminKey}`);

  console.log("adminKey");
  console.log(adminKey);

  // from seats io doc
  const appendedAminKey = adminKey + ":";
  const base64Key = window.btoa(appendedAminKey);
  return base64Key;
}

/**
 * Generates seats io holdToken from the frontend app
 * Later on, this should be using a backend endpoint that does the same job, because like this we're exposing seatsIo API Key
 * Worst thing that can happen if someone steals this API key is that he will make our seats booked or he will make our booked seats cancelled
 *
 */
export async function generateHoldToken() {
  console.log("hi from generateHoldToken");

  // Making holdToken live for two hours to cover most cases of new bookings
  const expiresInMinutes = 2 * 60;
  if (!POSConfigModule.groupInfo) {
    // no publicKey in the app, if ask seats io to generate a holdToken without specifying the workspaceKey then it will generate the holdToken for the default workspace which is for us the TEST account, and we will end up with a mess
    LogService.info(`no groupInfo found to get its seats io publicKey`);
    return;
  }
  const workSpaceKey = String(POSConfigModule.groupInfo?.seatsPublicKey);

  const response = await fetch("https://api.seats.io/hold-tokens", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Basic " + getAdminKey(),
      "X-Workspace-Key": workSpaceKey,
    },
    body: JSON.stringify({
      expiresInMinutes: expiresInMinutes,
    }),
  });
  console.log("response of seats.io/hold-token");
  console.log(response);
  const data = await response.json();
  console.log("data");
  console.log(data);
  return data;
}

/**
 * This function will make the holdToken expires immediately, why?
 * We use it when the user closes a booking that have unconfirmed pss, this means the chart had some held seats and we need to free them
 * Expiring the hold token, which will automatically release the objects: https://docs.seats.io/docs/api/release-objects
 */
export async function makeHoldTokenExpire(token: string) {
  // POST https://api-{region}.seatsio.net/hold-tokens/{token}
  console.log("hi from makeHoldTokenExpire");
  const response = await fetch(`https://api.seats.io/hold-tokens/${token}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Basic " + getAdminKey(),
    },
    body: JSON.stringify({
      expiresInMinutes: 0,
    }),
  });
}

/**
 * Call a seats io endpoint to ask it to release held seats (from status: `reservedByTokenand` to `free`)
 * used when a user remove an unconfirmed pss that has a corresponding hold seat
 */
export async function releaseHeldSeats(
  seats: any,
  eventKey: string,
  holdToken?: string
) {
  // Two endpoints (see https://docs.seats.io/docs/api/event-groups):
  //   - /events/{eventKey}/actions/release          — single event in URL path.
  //   - /events/groups/actions/release              — multi-event; events move
  //       into the body as `events: string[]`.
  // Detection: comma in eventKey → groups endpoint. Membership seating plan
  // (type 17) is the only producer of comma-separated eventKey today.
  console.log("--------------hi from releaseHeldSeats");
  const eventKeys = parseEventKeys(eventKey);
  const isMulti = eventKeys.length > 1;

  const url = isMulti
    ? `https://api.seats.io/events/groups/actions/release`
    : `https://api.seats.io/events/${eventKeys[0] ?? eventKey}/actions/release`;

  const body: {
    objects: any;
    holdToken?: string;
    events?: string[];
  } = { objects: seats };
  if (holdToken) body.holdToken = holdToken;
  if (isMulti) body.events = eventKeys;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Basic " + getAdminKey(),
    },
    body: JSON.stringify(body),
  });
}

/**
 * Call a seats io endpoint to ask it to release booked seats (from status: `booked` to `free`)
 * used when a user remove a confirmed pss that has a corresponding booked seat
 */
export async function releaseBookedSeats(
  seats: { seatId: string; eventKey: string }[] /*, holdToken: string*/
) {
  // Delegate to changeObjectsStatuses so multi-event statusChanges automatically
  // route to /events/groups/actions/change-object-status. Same status="free"
  // semantics as before; the seats.io call shape is identical to the previous
  // direct fetch for the single-event case.
  console.log("------ hi from releaseBookedSeats");
  // Preserve the legacy wire shape: `objects` is a plain string array of seatIds
  // (not `{ objectId, ticketType }` objects). seats.io accepts both, but the
  // string form is what the endpoint received historically — keep parity.
  // The `seats` accumulator inside changeObjectsStatuses will end up with noise
  // entries for this payload, but that accumulator only feeds `fullSeatData`,
  // which releaseBookedSeats does not read.
  const builtStatuses = seats.map((s) => ({
    event: s.eventKey,
    objects: [s.seatId],
    status: "free",
    orderId: String(BookingModule.selectedBooking?.reference),
  })) as any;
  console.log("calling change-object-status with payload:");
  console.log(builtStatuses);
  await changeObjectsStatuses(builtStatuses);
}

/**
 * @deprecated This function is not used anymore, we now use changeObjectsStatuses instead
 * Called after confirming a PSS so that seats io change the status of the corresponding seat from `reservedByTokenand` to `booked`
 */
export async function bookHeldSeats(seats: string[], eventKey: string) {
  // POST https://api-{region}.seatsio.net/events/{eventKey}/actions/book
  console.log("hi from bookHeldSeats");
  const response = await fetch(
    `https://api.seats.io/events/${eventKey}/actions/book?expand=objects`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Basic " + getAdminKey(),
      },
      body: JSON.stringify({
        objects: seats,
      }),
    }
  );
  // console.log("we got the response");
  // console.log(response);
  const data = await response.json();
  // console.log("data");
  // console.log(data);
  // lets now build fullSeatData object based on how many seats we have confirmed
  const fullSeatData: any = {}; //data.objects[0].labels || {};
  seats.forEach((s) => {
    if (data.objects[s]) {
      fullSeatData[s] = data.objects[s].labels;
    }
  });

  // console.log("fullSeatData");
  // console.log(fullSeatData);
  return fullSeatData;
}

/**
 * Call a seats io endpoint to update seats objects statuses at one shot
 * Important to know that: either all status changes get applied (in order), or none
 * Returns an object like this: {"seatNumber1-pssUuid1": {}, "seatNumber2-pssUuid2": {}}
 */
export async function changeObjectsStatuses(
  payload: {
    event: string;
    objects: { objectId: string; /*pssUuid*/ ticketType: string }[];
    status: string;
    holdToken?: string;
  }[]
) {
  // Two endpoints in play:
  //   - /events/actions/change-object-status         — array of statusChanges in
  //       `{ statusChanges: [...] }`; each item has `event: "<key>"`.
  //   - /events/groups/actions/change-object-status — one statusChange flat in the
  //       body root (no `statusChanges` wrapper). Body: `{ events: [...], objects,
  //       status, orderId, holdToken }`. One fetch per statusChange.
  // Detection is per-statusChange: if `event` contains a comma it carries multiple
  // event keys (membership seating plan, type 17) and must go through the groups
  // endpoint. Otherwise the regular endpoint is used as before. A single call to
  // changeObjectsStatuses can mix both shapes (we group and dispatch separately).
  console.log("---------------- hi from changeObjectsStatuses");

  const singleEventChanges: typeof payload = [];
  const multiEventChanges: {
    events: string[];
    objects: { objectId: string; ticketType: string }[];
    status: string;
    holdToken?: string;
  }[] = [];
  for (const sc of payload) {
    if (sc.event && sc.event.includes(",")) {
      const { event, ...rest } = sc;
      multiEventChanges.push({
        ...rest,
        events: parseEventKeys(event),
      });
    } else {
      singleEventChanges.push(sc);
    }
  }

  const semiFlattenedData: any[] = [];

  if (singleEventChanges.length) {
    const response = await fetch(
      `https://api.seatsio.net/events/actions/change-object-status?expand=objects`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Basic " + getAdminKey(),
        },
        body: JSON.stringify({
          statusChanges: singleEventChanges,
        }),
      }
    );
    const data = await response.json();
    console.log("data (single-event change-object-status)");
    console.log(data);
    LogService.info(
      `We got attached response from change-object-status (single-event)`,
      { params: data }
    );
    if (data && data.results) {
      semiFlattenedData.push(...data.results.map((r: any) => r.objects).flat());
    }
  }

  // Groups endpoint takes ONE statusChange flat in the body (no `statusChanges`
  // wrapper, no array). One fetch per multi-event statusChange. Response shape
  // is `{ objects: { seatId: {...} } }` rather than `{ results: [{ objects: ... }] }`,
  // so we wrap it to look like a single result before pushing into the shared
  // semiFlattenedData accumulator.
  if (multiEventChanges.length) {
    const groupsResponses = await Promise.all(
      multiEventChanges.map((sc) =>
        fetch(
          `https://api.seatsio.net/events/groups/actions/change-object-status?expand=objects`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Basic " + getAdminKey(),
            },
            body: JSON.stringify(sc),
          }
        ).then((r) => r.json())
      )
    );
    for (const data of groupsResponses) {
      console.log("data (multi-event change-object-status, groups endpoint)");
      console.log(data);
      LogService.info(
        `We got attached response from change-object-status (multi-event groups endpoint)`,
        { params: data }
      );
      if (data && data.objects) {
        semiFlattenedData.push(data.objects);
      } else if (data && data.results) {
        // Defensive fallback: in case the endpoint ever returns the wrapped shape.
        semiFlattenedData.push(
          ...data.results.map((r: any) => r.objects).flat()
        );
      }
    }
  }

  const seats: { seat: string; pssUuid: string }[] = payload
    .map((p) =>
      p.objects
        .map((o) => {
          return { seat: o.objectId, pssUuid: o.ticketType };
        })
        .flat()
    )
    .flat();
  console.log("seats");
  console.log(seats);

  console.log("--------- semiFlattenedData");
  console.log(semiFlattenedData);
  // lets now build fullSeatData object based on how many seats we have confirmed
  const fullSeatData: any = {}; //data.objects[0].labels || {};
  if (semiFlattenedData && semiFlattenedData.length) {
    seats.forEach((s) => {
      for (let i = 0; i < semiFlattenedData.length; i++) {
        if (semiFlattenedData[i][s.seat]) {
          if (
            semiFlattenedData[i][s.seat].objectType === "generalAdmission" ||
            semiFlattenedData[i][s.seat].objectType.includes("generalAdmission")
          ) {
            // We do not get pssUuid value in ticketType field for GA type in the response, so we use the seat name only
            fullSeatData[s.seat] = semiFlattenedData[i][s.seat].labels;
          } else {
            // so this is a seating type
            // in this type in the response we get back the field ticketType that we attached to it the pssUuid
            if (semiFlattenedData[i][s.seat].ticketType === s.pssUuid) {
              fullSeatData[s.seat + "-" + s.pssUuid] =
                semiFlattenedData[i][s.seat].labels;
            }
          }
        } else {
          continue;
        }
      }
    });
  }

  console.log("-------- fullSeatData");
  console.log(fullSeatData);
  return fullSeatData;
}
